import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { TrendingUp, CheckCircle, XCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const EstimateConversionReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    totalEstimates: 0,
    converted: 0,
    pending: 0,
    lost: 0,
    conversionRate: 0,
    avgConversionTime: 0,
    estimatesByStatus: [],
    monthlyConversion: []
  });
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    loadConversionData();
  }, []);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const loadConversionData = async () => {
    setLoading(true);
    try {
      // Get all estimates including notes
      const { data: allEstimates } = await supabase
        .from('estimates')
        .select(`
          id,
          estimate_number,
          estimate_date,
          total_amount,
          status,
          notes,
          customer_id,
          customers (first_name, last_name, company)
        `)
        .order('estimate_date', { ascending: false });

      // Get jobs with estimate references
      const { data: jobs } = await supabase
        .from('jobs')
        .select('estimate_id, created_at')
        .not('estimate_id', 'is', null);

      // Calculate metrics
      const estimatesWithJobs = new Set(jobs?.map(j => j.estimate_id) || []);

      const converted = allEstimates?.filter(e => estimatesWithJobs.has(e.id)) || [];
      const pending = allEstimates?.filter(e =>
        ['Draft', 'Sent'].includes(e.status) && !estimatesWithJobs.has(e.id)
      ) || [];
      const lost = allEstimates?.filter(e =>
        e.status === 'Cancelled' ||
        (e.status === 'Expired' && !estimatesWithJobs.has(e.id)) ||
        (e.status === 'Declined' && !estimatesWithJobs.has(e.id))
      ) || [];

      // Calculate average conversion time
      let totalConversionDays = 0;
      converted.forEach(estimate => {
        const job = jobs?.find(j => j.estimate_id === estimate.id);
        if (job) {
          const estimateDate = new Date(estimate.estimate_date);
          const jobDate = new Date(job.created_at);
          const days = Math.floor((jobDate - estimateDate) / (1000 * 60 * 60 * 24));
          totalConversionDays += days;
        }
      });
      const avgConversionTime = converted.length > 0 ? totalConversionDays / converted.length : 0;

      // Monthly conversion trend
      const monthlyConversion = await calculateMonthlyConversion(allEstimates, jobs);

      setReportData({
        totalEstimates: allEstimates?.length || 0,
        converted: converted.length,
        pending: pending.length,
        lost: lost.length,
        conversionRate: allEstimates?.length > 0
          ? ((converted.length / (allEstimates.length - pending.length)) * 100).toFixed(1)
          : 0,
        avgConversionTime: Math.round(avgConversionTime),
        estimatesByStatus: [
          {
            key: 'converted',
            status: 'Converted to Job',
            count: converted.length,
            value: converted.reduce((sum, e) => sum + parseFloat(e.total_amount || 0), 0),
            color: 'text-green-600',
            bgColor: 'bg-green-100',
            borderColor: 'border-green-200',
            icon: 'check',
            estimates: converted
          },
          {
            key: 'pending',
            status: 'Pending',
            count: pending.length,
            value: pending.reduce((sum, e) => sum + parseFloat(e.total_amount || 0), 0),
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-100',
            borderColor: 'border-yellow-200',
            icon: 'clock',
            estimates: pending
          },
          {
            key: 'lost',
            status: 'Lost / Cancelled / Declined',
            count: lost.length,
            value: lost.reduce((sum, e) => sum + parseFloat(e.total_amount || 0), 0),
            color: 'text-red-600',
            bgColor: 'bg-red-100',
            borderColor: 'border-red-200',
            icon: 'x',
            estimates: lost
          }
        ],
        monthlyConversion
      });
    } catch (error) {
      console.error('Error loading conversion data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyConversion = async (estimates, jobs) => {
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

      const monthEstimates = estimates?.filter(e => {
        const eDate = new Date(e.estimate_date);
        return eDate >= monthDate && eDate < nextMonth;
      }) || [];

      const estimateIds = new Set(monthEstimates.map(e => e.id));
      const convertedCount = jobs?.filter(j => estimateIds.has(j.estimate_id)).length || 0;

      months.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        total: monthEstimates.length,
        converted: convertedCount,
        rate: monthEstimates.length > 0 ? ((convertedCount / monthEstimates.length) * 100).toFixed(0) : 0
      });
    }

    return months;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const getCustomerName = (estimate) => {
    if (!estimate.customers) return 'Unknown';
    const { first_name, last_name, company } = estimate.customers;
    if (company) return company;
    return [first_name, last_name].filter(Boolean).join(' ') || 'Unknown';
  };

  const getDaysAgo = (dateStr) => {
    const days = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p>Loading conversion report...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Print Header */}
      <div className="print-only text-center mb-6">
        <h1 className="text-2xl font-bold">JFT Works</h1>
        <h2 className="text-xl">Estimate Conversion Report</h2>
        <p className="text-sm text-gray-600">
          As of {new Date().toLocaleDateString('en-CA')}
        </p>
      </div>

      {/* Screen Header */}
      <div className="no-print mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <TrendingUp size={32} />
          Estimate Conversion Report
        </h1>
        <p className="text-gray-600 mt-2">Track estimate win rates and sales effectiveness</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Total Estimates</h3>
          <div className="text-4xl font-bold text-gray-900">{reportData.totalEstimates}</div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Conversion Rate</h3>
            <CheckCircle className="text-green-500" size={24} />
          </div>
          <div className="text-4xl font-bold text-green-600">{reportData.conversionRate}%</div>
          <p className="text-sm text-gray-500 mt-1">{reportData.converted} won</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Pending</h3>
            <Clock className="text-yellow-500" size={24} />
          </div>
          <div className="text-4xl font-bold text-yellow-600">{reportData.pending}</div>
          <p className="text-sm text-gray-500 mt-1">awaiting decision</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Avg. Time to Convert</h3>
            <TrendingUp className="text-blue-500" size={24} />
          </div>
          <div className="text-4xl font-bold text-blue-600">{reportData.avgConversionTime}</div>
          <p className="text-sm text-gray-500 mt-1">days</p>
        </div>
      </div>

      {/* Breakdown by Status — Accordion */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Estimates by Status</h3>
        <div className="space-y-3">
          {reportData.estimatesByStatus.map((item) => {
            const isOpen = !!expandedSections[item.key];
            return (
              <div key={item.key} className={`rounded-lg border ${item.borderColor} overflow-hidden`}>
                {/* Header row — clickable */}
                <button
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => toggleSection(item.key)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded ${item.bgColor}`}>
                      {item.icon === 'check' && <CheckCircle className={item.color} size={20} />}
                      {item.icon === 'clock' && <Clock className={item.color} size={20} />}
                      {item.icon === 'x' && <XCircle className={item.color} size={20} />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.status}</p>
                      <p className="text-sm text-gray-500">{item.count} estimate{item.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className={`text-2xl font-bold ${item.color}`}>{formatCurrency(item.value)}</p>
                    {item.count > 0 && (
                      isOpen
                        ? <ChevronDown className="text-gray-400" size={20} />
                        : <ChevronRight className="text-gray-400" size={20} />
                    )}
                  </div>
                </button>

                {/* Detail rows */}
                {isOpen && item.count > 0 && (
                  <div className="border-t border-gray-100">
                    <table className="w-full text-sm">
                      <thead className={`${item.bgColor}`}>
                        <tr>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Estimate #</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Customer</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Date</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Age</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Status</th>
                          <th className="text-right px-4 py-2 font-semibold text-gray-700">Amount</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Notes / Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {item.estimates.map((est) => (
                          <tr key={est.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-mono text-gray-800">{est.estimate_number}</td>
                            <td className="px-4 py-2 text-gray-800">{getCustomerName(est)}</td>
                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                              {new Date(est.estimate_date).toLocaleDateString('en-CA')}
                            </td>
                            <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                              {getDaysAgo(est.estimate_date)}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${item.bgColor} ${item.color}`}>
                                {est.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right font-semibold text-gray-800 whitespace-nowrap">
                              {formatCurrency(est.total_amount)}
                            </td>
                            <td className="px-4 py-2 text-gray-500 max-w-xs">
                              {est.notes
                                ? <span title={est.notes} className="block truncate">{est.notes}</span>
                                : <span className="italic text-gray-400">No notes</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly Conversion Trend */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Conversion Trend</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Month</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Total Estimates</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Converted</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-700">Conversion Rate</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Visual</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reportData.monthlyConversion.map((month, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-900">{month.month}</td>
                  <td className="py-3 px-4 text-center text-gray-900">{month.total}</td>
                  <td className="py-3 px-4 text-center text-green-600 font-semibold">{month.converted}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      month.rate >= 50 ? 'bg-green-100 text-green-800' :
                      month.rate >= 30 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {month.rate}%
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${month.rate}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          body, html, * {
            background: white !important;
          }
        }
        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EstimateConversionReport;
